// src/components/ProcessManager/CommercialRegister/ProcessManagerCommercialRegisterTableRow.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { IProcessManager } from '../../Interfaces/IProcessManager';
import { ILegalTransactionRelationship } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import {
  IconButton,
  TableCell,
  TableRow,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Collapse,
} from '@mui/material';
import {
  Check,
  Close,
  Delete,
  ContentCopy,
  Visibility,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { CustomeTextField } from '../../generic/CustomeTextField';
import { ProcessManagerCommercialRegisterResult } from './ProcessManagerCommercialRegisterResult';
import CopyAllIcon from '@mui/icons-material/CopyAll';
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import { AddPMAutomation } from '../Automations/AddPMAutomation';
import { IProcessManagerAutomation } from '../../Interfaces/IProcessManagerAutomation';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { ILegalPhaseFullObject } from '../../Interfaces/ILegalPhase';
import { ILegalRequirementState } from '../../Interfaces/ILegalRequirementState';
import { PMAutomationsTable } from '../Automations/PMAutomationsTable';

type RegisterPM = IProcessManager & { idProcessManagerType: 20 | 21 };

interface IProps {
  processManager: RegisterPM;
  processManagerArray: IProcessManager[];
  setProcessManagerArray: Function;
  relationsships: ILegalTransactionRelationship[];
  setRelationships: Function;
  //
  legalClients: ILegalClient[];
  legalPhases: ILegalPhaseFullObject[];
  legalReqStates: ILegalRequirementState[];
}

export const ProcessManagerCommercialRegisterTableRow: React.FC<IProps> = ({
  processManager,
  processManagerArray,
  setProcessManagerArray,
  relationsships,
  setRelationships,
  legalClients,
  legalPhases,
  legalReqStates,
}) => {
  const [editPM, setEditPM] = useState(processManager);
  const [openResult, setOpenResult] = useState(false);
  //
  const [isExpantedAutomations, setIsExpantedAutomations] = useState(false);

  useEffect(() => {
    if (editPM.idProcessManagerType === 20) {
      const raw = (editPM.data as any)?.ChangeKind;
      if (raw === undefined || raw === null || raw === '') {
        setEditPM(prev => ({
          ...prev,
          data: { ...(prev.data ?? {}), ChangeKind: 0 },
        }));
      }
    }
  }, []);

  useEffect(() => {
    setProcessManagerArray((prev: { idProcessManager: number }[]) =>
      prev.map((pm: { idProcessManager: number }) =>
        pm.idProcessManager === editPM.idProcessManager ? editPM : pm
      )
    );
  }, [editPM, setProcessManagerArray]);

  const handleRemove = () => {
    setProcessManagerArray((prev: any[]) =>
      prev.filter(pm => pm.idProcessManager !== editPM.idProcessManager)
    );
  };

  const handleCopy = () => {
    const today = new Date();
    const toDateStr = today.toISOString().slice(0, 10);
    const nowStr = today.toISOString().slice(0, 19).replace('T', ' ');

    const sourceData: any = editPM.data ?? {};
    const { idLegalClient, ChangeKind } = sourceData;

    const cleanedData: any = {
      ...(sourceData ?? {}),
      idLegalClient: idLegalClient ?? editPM.idLegalClient ?? null,
      ChangeKind: ChangeKind ?? 0,
    };
    delete cleanedData.lastChange;
    delete cleanedData.dkLastDetection;
    delete cleanedData.idDocument;

    const newPM: RegisterPM = {
      ...editPM,
      idProcessManager: -1,
      idProcessManagerState: 1,
      Tries: 0,
      ExitCodeLastTry: null as any,
      LastTry_at: null as any,
      Closed_at: null as any,
      Updated_at: nowStr as any,
      DateToCheck: toDateStr as any,
      data: cleanedData,
    };

    setProcessManagerArray((prev: any) => [newPM, ...prev]);
  };

  // ---- NEW: RESULT OPEN ----
  const handleOpenResult = () => setOpenResult(true);
  const handleCloseResult = () => setOpenResult(false);

  const legalClientId: number | null =
    (editPM.data as any)?.idLegalClient ?? editPM.idLegalClient ?? null;

  const relatedMember = useMemo(() => {
    return legalClientId
      ? relationsships
          .flatMap(r => r.Members ?? [])
          .find(m => m.idLegalClient === legalClientId)
      : undefined;
  }, [relationsships, legalClientId]);

  const lc = relatedMember?.LegalClient;

  const registerCourt: string = lc?.Court ?? '—';
  const registerNumber: string = lc?.RegisterNumber ?? '—';
  const companyName: string =
    lc?.LastName?.trim() ||
    lc?.SurName?.trim() ||
    [lc?.FirstName, lc?.LastName].filter(Boolean).join(' ').trim() ||
    '—';

  const d: any = editPM.data ?? {};
  const KIND_TO_NUM = { Eintragung: 0, Veränderung: 1, Löschung: 2 } as const;
  const NUM_TO_KIND = {
    0: 'Eintragung',
    1: 'Veränderung',
    2: 'Löschung',
  } as const;

  const rawKind = d?.ChangeKind;
  const uiKind: 'Eintragung' | 'Veränderung' | 'Löschung' =
    typeof rawKind === 'number' &&
    (rawKind === 0 || rawKind === 1 || rawKind === 2)
      ? NUM_TO_KIND[rawKind as 0 | 1 | 2]
      : rawKind === 'Eintragung' ||
          rawKind === 'Veränderung' ||
          rawKind === 'Löschung'
        ? rawKind
        : 'Eintragung';

  const handleChangeKind = (
    value: 'Eintragung' | 'Veränderung' | 'Löschung'
  ) => {
    setEditPM(prev => ({
      ...prev,
      data: { ...(prev.data ?? {}), ChangeKind: KIND_TO_NUM[value] },
    }));
  };

  const isClosed = editPM.idProcessManagerState !== 1;

  return (
    <>
      <TableRow>
        <TableCell>{isClosed ? <Check /> : <Close />}</TableCell>
        <TableCell>
          {editPM.Automations.length === 0 ? (
            <AddPMAutomation
              processManager={editPM}
              automations={editPM.Automations}
              setAutomations={(
                updatedAutomations: IProcessManagerAutomation[]
              ) =>
                setEditPM({
                  ...editPM,
                  Automations: updatedAutomations,
                })
              }
              legalClients={legalClients}
              legalPhases={legalPhases}
              legalReqStates={legalReqStates}
              setIsExpantedAutomations={setIsExpantedAutomations}
            />
          ) : isExpantedAutomations ? (
            <IconButton
              size='small'
              onClick={() => setIsExpantedAutomations(false)}
            >
              <ExpandLess />
            </IconButton>
          ) : (
            <IconButton
              size='small'
              onClick={() => setIsExpantedAutomations(true)}
            >
              <ExpandMore />
            </IconButton>
          )}
        </TableCell>

        <TableCell>{editPM.Tries}</TableCell>

        <TableCell>
          <CustomeTextField
            object={editPM}
            attr='DateToCheck'
            label='Änderungen ab'
            setObject={setEditPM}
            type='date'
            disabled={isClosed}
          />
        </TableCell>

        {editPM.idProcessManagerType === 20 && (
          <TableCell>
            <TextField
              select
              label='Vorgang'
              value={uiKind}
              onChange={e =>
                handleChangeKind(
                  e.target.value as 'Eintragung' | 'Veränderung' | 'Löschung'
                )
              }
              size='small'
              fullWidth
              disabled={isClosed}
            >
              <MenuItem value='Eintragung'>Eintragung </MenuItem>
              <MenuItem
                value='Veränderung'
                disabled={lc?.RegisterNumber == undefined}
              >
                Veränderung
                {lc?.RegisterNumber == undefined &&
                  ' (Bitte Registernummer eintragen)'}
              </MenuItem>
              <MenuItem
                value='Löschung'
                disabled={lc?.RegisterNumber == undefined}
              >
                Löschung
                {lc?.RegisterNumber == undefined &&
                  ' (Bitte Registernummer eintragen)'}
              </MenuItem>
            </TextField>
          </TableCell>
        )}

        <TableCell>{registerCourt}</TableCell>
        <TableCell>{registerNumber}</TableCell>
        <TableCell>{companyName}</TableCell>

        <TableCell>
          {isClosed ? (
            <>
              <IconButton size='small' title='Kopieren' onClick={handleCopy}>
                <CopyAllIcon />
              </IconButton>
              <IconButton
                size='small'
                title='Details'
                onClick={handleOpenResult}
              >
                <ContentPasteSearchIcon />
              </IconButton>
            </>
          ) : (
            <IconButton
              size='small'
              title='Löschen'
              disabled={false}
              onClick={handleRemove}
            >
              <Delete />
            </IconButton>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={2} style={{ padding: 0 }} />
        <TableCell colSpan={7} style={{ padding: 0 }}>
          <Collapse in={isExpantedAutomations}>
            <PMAutomationsTable
              processManager={editPM}
              automations={editPM.Automations}
              setAutomations={(
                updatedAutomations: IProcessManagerAutomation[]
              ) =>
                setEditPM({
                  ...editPM,
                  Automations: updatedAutomations,
                })
              }
              legalClients={legalClients}
              legalPhases={legalPhases}
              legalReqStates={legalReqStates}
              setIsExpantedAutomations={setIsExpantedAutomations}
            />
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Result-Dialog */}
      <Dialog
        open={openResult}
        onClose={handleCloseResult}
        fullWidth
        maxWidth='md'
      >
        <DialogTitle>Handelsregister – Ergebnis</DialogTitle>
        <DialogContent dividers>
          <ProcessManagerCommercialRegisterResult processManager={editPM} />
        </DialogContent>
      </Dialog>
    </>
  );
};
